import React, { useState } from "react";
import PropTypes from "prop-types";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";

// Inline moderation actions for one feed post, embedded per-row in the admin
// queue. Mirrors the Request ApprovalPanel's action set (approve / ghost /
// unghost / resubmit) but far simpler — posts have no distribution tiers,
// free-to-pitch, or edit review. The admin API is token-authed with
// skip_forgery_protection, so only the bearer header is needed (no CSRF token).
function FeedPostApprovalPanel({ post, onActionComplete }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isGhosted = Boolean(post.ghosted_at);
  const isPending = Boolean(post.needs_manual_approval_at) && !post.approved_at && !isGhosted;

  const performAction = async (action, method = "POST") => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/admin/api/feed_posts/${post.id}/${action}`, {
        method,
        headers: { ...authenticatedHeadersHash(), "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || `Action failed (${res.status})`);
        return;
      }
      onActionComplete?.();
    } catch {
      setError("Network error — please retry");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-1" style={{ minWidth: 160 }}>
      <div className="d-flex flex-wrap gap-1">
        {isPending && (
          <button
            type="button"
            className="btn btn-sm btn-success"
            disabled={busy}
            onClick={() => performAction("approve")}
          >
            Approve
          </button>
        )}
        {!isGhosted && (
          <button
            type="button"
            className="btn btn-sm btn-danger"
            disabled={busy}
            onClick={() => performAction("ghost")}
          >
            Ghost
          </button>
        )}
        {isGhosted && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={busy}
            onClick={() => performAction("unghost")}
          >
            Unghost
          </button>
        )}
        {isPending && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            disabled={busy}
            onClick={() => performAction("resubmit")}
          >
            Re-evaluate
          </button>
        )}
        {isPending && !post.assigned_to_id && (
          <button
            type="button"
            className="btn btn-sm btn-outline-info"
            disabled={busy}
            onClick={() => performAction("assign")}
          >
            Assign to me
          </button>
        )}
      </div>
      {error && <div className="text-danger small">{error}</div>}
    </div>
  );
}

FeedPostApprovalPanel.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    approved_at: PropTypes.string,
    ghosted_at: PropTypes.string,
    needs_manual_approval_at: PropTypes.string,
    assigned_to_id: PropTypes.string,
  }).isRequired,
  onActionComplete: PropTypes.func,
};

export default FeedPostApprovalPanel;
